import p5 from "p5";
import { animationFrameScheduler, auditTime, Subject, takeUntil } from "rxjs";

import { CanvasSize } from "@models/board.model.ts";
import { BACKGROUND_COLOR, DotColor } from "@models/colors.model.ts";
import { TransitionPhase } from "@models/animation.model.ts";

import { DefaultBoardConfig, Dot } from "@config/board.config.ts";

import { fontData, FONT_WIDTH } from "@helpers/dotFont.ts";
import { debug } from "@helpers/logger";

abstract class DotMatrix {
  public destroy$: Subject<void> = new Subject<void>();

  protected redraw$ = new Subject<void>();

  protected dotRadius: number = Dot.Radius;
  protected dotGap: number = Dot.Gap;
  protected dotUnit: number = Dot.Unit;
  protected dotColumnCount: number;
  protected dotRowCount: number;
  protected textField = { top: 0, right: 0, bottom: 0, left: 0 };
  protected scale: number = 1;
  protected canvasSize: CanvasSize = { width: 0, height: 0 };
  protected isInTransition = true;
  protected transitionPhase = TransitionPhase.None;
  protected grid: p5.Graphics | null = null;

  constructor(numRows: number, numCols: number) {
    this.dotColumnCount = numCols;
    this.dotRowCount = numRows;
  }

  protected abstract setCanvasSize(): void;
  protected abstract drawContent(p: p5): void;

  /**
   * Standardized p5 setup wrapper
   */
  protected createSketch() {
    return (p: p5) => {
      p.setup = () => {
        p.createCanvas(this.canvasSize.width, this.canvasSize.height);
        p.noLoop();

        p.windowResized = () => {
          this.setCanvasSize();
          p.resizeCanvas(this.canvasSize.width, this.canvasSize.height);

          this.rebuildGrid(p);
          p.redraw();
        };

        this.rebuildGrid(p);
        this.setupSubscriptions(p);
      };

      p.draw = () => {
        p.ellipseMode(p.RADIUS);
        p.scale(this.scale);
        p.background(BACKGROUND_COLOR);
        p.noFill();

        if (this.grid) {
          p.image(this.grid, 0, 0);
        }

        this.drawLitBoard(p);
        this.drawContent(p);
      };
    };
  }

  protected setupSubscriptions(p: p5) {
    this.redraw$.pipe(auditTime(0, animationFrameScheduler), takeUntil(this.destroy$)).subscribe(() => p.redraw());

    this.destroy$.subscribe(() => {
      debug("destroying...");
    });
  }

  /**
   * Cache the background board in a graphics buffer
   * @param p - p5.js instance
   */
  protected rebuildGrid(p: p5) {
    this.grid?.remove();

    const g = p.createGraphics(this.dotColumnCount * this.dotUnit - this.dotGap, this.dotRowCount * this.dotUnit - this.dotGap);
    g.ellipseMode(g.RADIUS);
    g.background(BACKGROUND_COLOR);
    g.fill(DotColor.Off);

    for (let row = 0; row < this.dotRowCount; row++) {
      for (let col = 0; col < this.dotColumnCount; col++) {
        g.ellipse(this.dotRadius + col * this.dotUnit, this.dotRadius + row * this.dotUnit, this.dotRadius, this.dotRadius);
      }
    }

    this.grid = g;
  }

  protected drawLitBoard(p: p5) {
    let lastFill = "";

    for (let row = 0; row < this.dotRowCount; row++) {
      const dotY = this.dotRadius + row * this.dotUnit;

      for (let col = 0; col < this.dotColumnCount; col++) {
        const color = this.backgroundDotColor(row, col);
        if (color === DotColor.Off) {
          continue;
        }
        if (color !== lastFill) {
          p.fill(color);
          lastFill = color;
        }

        p.ellipse(this.dotRadius + col * this.dotUnit, dotY, this.dotRadius, this.dotRadius);
      }
    }
  }

  protected backgroundDotColor(_row: number, _col: number): string {
    return DotColor.Off;
  }

  /**
   * Render a single character on the dot matrix display
   * Only renders dots within the visible display area
   * @param p - p5.js instance
   * @param char - Character to render
   * @param startX - Starting X position in pixels
   * @param startY - Starting Y position in pixels
   * @param color - Color to render the char in
   */
  protected renderChar(p: p5, char: string, startX: number, startY: number, color: string) {
    const charMatrix = fontData.get(char.toUpperCase()) || fontData.get(" ") || []; // Default to space if character not found
    const { top, right, bottom, left } = this.textField;

    p.fill(color);

    let dotY = startY;

    for (const row of charMatrix) {
      let dotX = startX;

      for (const dotVal of row) {
        if (dotVal === 1 && dotY >= top && dotX <= right && dotY <= bottom && dotX >= left) {
          p.ellipse(dotX, dotY, this.dotRadius, this.dotRadius);
        }

        dotX += this.dotUnit;
      }

      dotY += this.dotUnit;
    }
  }

  /**
   * Check if a message is too long to fit on the display
   * @param message - The message to check
   * @returns True if the message needs to scroll, false otherwise
   */
  protected messageOverflow(message: string): boolean {
    const msgWidth = getMessageLength(message);
    const visibleWidth = (this.textField.right - this.textField.left + this.dotGap) / this.dotUnit;

    return msgWidth > visibleWidth;
  }
}

/**
 * Calculate the length of a message in number of dots
 * @param message - The message to measure
 * @returns Length of the message in dot matrix columns
 */
export function getMessageLength(message: string): number {
  return message.length * (FONT_WIDTH + DefaultBoardConfig.CharGap) - DefaultBoardConfig.CharGap;
}

export default DotMatrix;
